import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  PencilIcon, 
  TrashIcon, 
  ArrowPathIcon,
  DocumentArrowDownIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Pagination from '../../components/common/Pagination';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('ru', ru);

interface Client {
  id: number;
  name: string;
}

interface Order {
  id: number;
  orderNumber: string;
  clientId: number;
  Client?: Client;
}

interface Payment {
  id: number;
  orderId: number;
  amount: number;
  date: string;
  method: string;
  referenceNumber?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  Order?: Order;
}

interface PaginatedPaymentsResponse {
  payments: Payment[];
  totalPages: number;
  currentPage: number;
  totalCount: number;
}

const PaymentList: React.FC = () => {
  const { can } = useAuth(); // Changed from isAllowed
  const [searchParams, setSearchParams] = useSearchParams();

  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for pagination and filters (synced with URL)
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [startDate, setStartDate] = useState<Date | null>(
    searchParams.get('from') ? new Date(searchParams.get('from')!) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    searchParams.get('to') ? new Date(searchParams.get('to')!) : null
  );
  const [limit] = useState(20); // Items per page

  // --- Permissions ---
  const canAdd = can('payments.create'); // Changed from isAllowed
  const canEdit = can('payments.edit'); // Changed from isAllowed
  const canDelete = can('payments.delete'); // Changed from isAllowed
  const canGenerateReceipt = can('payments.generate_receipt'); // Changed from isAllowed, new permission

  // --- Data Fetching with Filters/Pagination ---
  const fetchPayments = useCallback(async (page = currentPage, search = searchTerm, dateFrom = startDate, dateTo = endDate) => {
    setLoading(true);
    setError(null);
    
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    if (search) params.append('search', search);
    if (dateFrom) params.append('from', dateFrom.toISOString().split('T')[0]);
    if (dateTo) params.append('to', dateTo.toISOString().split('T')[0]);

    // Update URL search params
    setSearchParams(params, { replace: true });

    try {
      // Use the correct API endpoint - assuming axios handles the /api prefix
      const response = await axios.get<PaginatedPaymentsResponse>('/payments', { params });
      setPayments(response.data.payments);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalCount(response.data.totalCount);
    } catch (err) {
      console.error('Error fetching payments:', err);
      setError('Ошибка при загрузке платежей');
      setPayments([]);
      setTotalPages(1);
      setCurrentPage(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [limit, setSearchParams, currentPage, searchTerm, startDate, endDate]);

  useEffect(() => {
    // Fetch data when component mounts or core params change
    // Format dates before fetching
    // Pass Date objects directly to fetchPayments
    fetchPayments(currentPage, searchTerm, startDate, endDate);
  }, [fetchPayments, currentPage, searchTerm, startDate, endDate]);

  // --- Handlers ---
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    // Debounce or fetch on button click? For simplicity, fetch on param change for now
    // We trigger fetch via useEffect watching searchTerm
  };

  // Handler for the Date Range Picker
  const handleDateRangeChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1); // Reset to page 1 when date range changes
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Trigger fetch via useEffect watching currentPage
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
    // Fetch is triggered by useEffect watching these states
  };

  const handleDelete = async (id: number) => {
    if (!canDelete) return; // Permission check
    if (window.confirm('Вы уверены, что хотите удалить этот платеж? Это действие не отменить.')) {
      try {
        await axios.delete(`/payments/${id}`);
        // Refetch current page data after deletion
        fetchPayments(currentPage, searchTerm, startDate, endDate); 
      } catch (err: any) {
        console.error('Error deleting payment:', err);
        alert(err.response?.data?.message || 'Ошибка при удалении платежа');
      }
    }
  };
  
  const handleGenerateReceipt = async (id: number) => {
    if (!canGenerateReceipt) return; // Permission check
    try {
        // Assuming backend returns blob for direct download/view
        const response = await axios.get(`/payments/${id}/receipt`, { responseType: 'blob' });
        const file = new Blob([response.data], { type: 'application/pdf' });
        const fileURL = URL.createObjectURL(file);
        window.open(fileURL, '_blank'); 
    } catch (err: any) {
        console.error('Error generating receipt:', err);
        alert(err.response?.data?.message || 'Ошибка при создании квитанции');
    }
  };
  
  // Filter payments safely, ensuring payments is an array
  const filteredPayments = Array.isArray(payments) ? payments.filter(payment => {
    // Date filter
    if (startDate && endDate) {
      const paymentDate = new Date(payment.date);
      const fromDate = new Date(startDate);
      const toDate = new Date(endDate);
      
      // Adjust time for inclusive range check
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      
      if (paymentDate < fromDate || paymentDate > toDate) {
        return false;
      }
    } else if (startDate) {
      const paymentDate = new Date(payment.date);
      const fromDate = new Date(startDate);
      fromDate.setHours(0, 0, 0, 0);
      
      if (paymentDate < fromDate) {
        return false;
      }
    } else if (endDate) {
      const paymentDate = new Date(payment.date);
      const toDate = new Date(endDate);
      toDate.setHours(23, 59, 59, 999);
      
      if (paymentDate > toDate) {
        return false;
      }
    }
    
    // Search filter
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (payment.referenceNumber && payment.referenceNumber.toLowerCase().includes(searchLower)) ||
      (payment.Order?.orderNumber && payment.Order.orderNumber.toLowerCase().includes(searchLower)) ||
      (payment.Order?.Client?.name && payment.Order.Client.name.toLowerCase().includes(searchLower)) ||
      payment.method.toLowerCase().includes(searchLower) ||
      String(payment.id).includes(searchTerm) ||
      String(payment.amount).includes(searchTerm)
    );
  }) : []; // Return empty array if payments is not an array
  
  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Неверная дата';
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return 'Ошибка даты';
    }
  };
  
  const formatCurrency = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 2 // Changed to 2
    }).format(amount);
  };
  
  // Add check for initial loading state
  if (loading && (!payments || payments.length === 0)) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Платежи"
        subtitle={`Всего найдено: ${totalCount}`}
        actions={
          canAdd ? (
            <Link
              to="/payments/new"
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-1" />
              Добавить платеж
            </Link>
          ) : null
        }
      />
      
      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded-md mb-4">
          {error}
        </div>
      )}
      
      {/* Filters Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {/* Search box */}
            <div className="w-full">
            <label htmlFor="search" className="form-label">Поиск</label>
            <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                <input
                id="search"
                type="text"
                placeholder="Номер, клиент, сумма..."
                className="form-input pl-10 w-full"
                value={searchTerm}
                onChange={handleSearchChange} 
                // Removed direct fetch call - handled by useEffect
                />
            </div>
            </div>
            
            {/* Date range filter */}
            <div className="w-full lg:col-span-2"> {/* Span 2 columns on larger screens */} 
              <label htmlFor="date-range" className="form-label">Период платежей</label>
              <DatePicker
                selectsRange={true}
                startDate={startDate}
                endDate={endDate}
                onChange={handleDateRangeChange}
                isClearable={true}
                dateFormat="dd.MM.yyyy"
                locale={ru}
                placeholderText="Выберите диапазон дат"
                className="form-input w-full"
                wrapperClassName="w-full"
                popperPlacement="bottom-start"
              />
            </div>
            
            {/* Action Buttons for Filters */}
            <div className="w-full flex space-x-2">
                {/* Apply Filters (implicitly via useEffect) */} 
                {/* <button 
                    type="button"
                    onClick={() => fetchPayments(1)} // Reset to page 1 when applying filters
                    className="btn-primary w-full flex items-center justify-center"
                    disabled={loading}
                >
                    <MagnifyingGlassIcon className="h-5 w-5 mr-1" />
                    Применить
                </button> */} 
                <button
                    type="button"
                    onClick={resetFilters}
                    className="btn-secondary-outline w-full flex items-center justify-center text-sm"
                    title="Сбросить фильтры"
                    disabled={loading}
                >
                    <XMarkIcon className="h-5 w-5 mr-1" />
                    Сбросить
                </button>
            </div>
        </div>
      </div>
      
      {/* Payments table */}
       {loading && <LoadingSpinner />} 
      
      {!loading && !error && (!filteredPayments || filteredPayments.length === 0) && (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-gray-500">
            {(searchTerm || startDate || endDate) 
              ? 'Платежи, соответствующие критериям, не найдены.' 
              : 'Платежи еще не добавлены.'}
          </p>
          {canAdd && (
            <Link to="/payments/new" className="mt-4 btn-primary inline-flex items-center">
              <PlusIcon className="h-5 w-5 mr-1" />
              Добавить платеж
            </Link>
          )}
        </div>
      )}

      {!loading && filteredPayments && filteredPayments.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">ID</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Дата</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Заказ</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Клиент</th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Сумма</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Метод</th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Референс</th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Действия</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-3 py-4 text-sm text-gray-500 text-center">{payment.id}</td>
                  <td className="px-4 py-4 text-sm text-gray-900">{formatDate(payment.date)}</td>
                  {/* Added break-words */}
                  <td className="px-4 py-4 text-sm break-words">
                    {payment.Order ? (
                      <Link to={`/orders/${payment.orderId}`} className="text-primary hover:underline">
                        #{payment.Order.orderNumber}
                      </Link>
                    ) : (
                      <span className="text-gray-400">#{payment.orderId}</span>
                    )}
                  </td>
                  {/* Added break-words */}
                  <td className="px-4 py-4 text-sm break-words">
                    {payment.Order?.Client ? (
                       <Link to={`/clients/${payment.Order.clientId}`} className="text-primary hover:underline">
                         {payment.Order.Client.name}
                       </Link>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 font-medium text-right">{formatCurrency(payment.amount)}</td>
                  {/* Added break-words */}
                  <td className="px-4 py-4 text-sm text-gray-500 break-words">{payment.method}</td>
                  {/* Added break-words */}
                  <td className="px-4 py-4 text-sm text-gray-500 break-words">{payment.referenceNumber || '-'}</td> 
                  <td className="px-4 py-4 text-center text-sm font-medium">
                    <div className="flex items-center justify-center space-x-2">
                      {canGenerateReceipt && (
                        <button 
                            onClick={() => handleGenerateReceipt(payment.id)} 
                            className="text-indigo-600 hover:text-indigo-900" 
                            title="Скачать квитанцию"
                        >
                            <DocumentArrowDownIcon className="h-5 w-5" />
                          </button>
                      )}
                      {canEdit && (
                        <Link to={`/payments/edit/${payment.id}`} className="text-blue-600 hover:text-blue-900" title="Редактировать">
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                      )}
                      {canDelete && (
                        <button 
                            onClick={() => handleDelete(payment.id)} 
                            className="text-red-600 hover:text-red-900" 
                            title="Удалить"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {/* Pagination */}
          {totalPages > 1 && (
             <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentList;
